import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Continue to Give vs Devotion Fund: A Brutally Honest Comparison (2026)';
const DESCRIPTION =
    "An honest, founder-written comparison — Continue to Give's lower raw fees vs Devotion Fund's recurring-first tooling — and exactly when each one is the right call.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover4.jpg',
            datePublished: '2026-07-20',
            dateModified: '2026-07-20',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/continue-to-give-vs-devotion-fund',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is Continue to Give cheaper than Devotion Fund?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Usually, on raw fees. CTG charges 2.7% + $0.25 (via a sending org) or 3.9% + $0.45 (independent), both typically lower than Devotion Fund's 5-7% at common gift sizes. Devotion Fund's fee is a single all-in number (5% bank transfer, 7% card) with no tiers.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Does Continue to Give offer tax-deductible donations?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, when processed through a sending organization. Devotion Fund's recurring partnership giving is not currently tax-deductible.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Which is better for an independent missionary with no sending organization?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "If keeping supporters engaged long-term is the harder problem than the fee difference, Devotion Fund's recurring-first design and built-in update tooling are built for exactly that. If the lowest possible fee and full fund accounting matter more, Continue to Give wins.",
                    },
                },
            ],
        },
    ],
};

export default function ContinueToGiveVsDevotionFund() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/continue-to-give-vs-devotion-fund' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-ctg-vs-df-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover4.jpg" alt="A wide open sky over farmland" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>I run Devotion Fund, so you already know which way I'm biased. This is my attempt to give you the comparison I'd want if I were the one choosing — including the parts where Continue to Give is the better call.</p>

                    <p>Continue to Give is one of the few platforms that has taken missionaries seriously for a long time. It's been around since 2010, it's faith-based, the founder built it partly to fund his own mission work in Africa, and — unlike a lot of giving platforms — it explicitly won't drop you over what you believe. That matters, and I'm not going to pretend it doesn't.</p>

                    <p>So this isn't a hit piece. It's an honest read on two platforms trying to solve overlapping problems in different ways.</p>

                    <h2>The 30-second version</h2>

                    <table>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Continue to Give</th>
                                <th>Devotion Fund</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>All-in transaction rate (missionary)</strong></td>
                                <td>2.7% + $0.25 via a sending org that uses CTG; 3.9% + $0.45 if independent</td>
                                <td>5% (bank transfer) / 7% (card)</td>
                            </tr>
                            <tr>
                                <td><strong>Monthly fee (giving only)</strong></td>
                                <td>$0</td>
                                <td>$0</td>
                            </tr>
                            <tr>
                                <td><strong>Recurring-first design</strong></td>
                                <td>Supported, but built on a churches-and-nonprofits chassis</td>
                                <td>Recurring-first by design</td>
                            </tr>
                            <tr>
                                <td><strong>Tax-deductible receipts</strong></td>
                                <td>Yes, through a sending organization</td>
                                <td>Not yet — recurring partnership giving is not tax-deductible</td>
                            </tr>
                            <tr>
                                <td><strong>Donor management / accounting</strong></td>
                                <td>Full ChMS + fund accounting (paid add-ons)</td>
                                <td>Built-in donor CRM; no fund accounting</td>
                            </tr>
                            <tr>
                                <td><strong>Update / newsletter tooling</strong></td>
                                <td>Newsletters in the paid ChMS module</td>
                                <td>Built-in, with AI-assisted drafting</td>
                            </tr>
                            <tr>
                                <td><strong>Built specifically for</strong></td>
                                <td>Churches, nonprofits, and missionaries</td>
                                <td>Support-raised missionaries and ministry workers, full stop</td>
                            </tr>
                            <tr>
                                <td><strong>Track record</strong></td>
                                <td>15+ years</td>
                                <td>New (launched 2026)</td>
                            </tr>
                        </tbody>
                    </table>

                    <p><small>Pricing verified against Continue to Give's own missionary pricing page. Always confirm the current number on the live page before you decide — platforms change rates.</small></p>

                    <h2>Let's start with the part that doesn't favor me: fees</h2>

                    <p>Here's Continue to Give's actual published missionary pricing, straight from their page:</p>

                    <ul>
                        <li><strong>Via a sending organization that uses CTG:</strong> 2.7% + $0.25 per transaction, $0/month.</li>
                        <li><strong>Independent (no sending org, or your org doesn't use CTG):</strong> 3.9% + $0.45 per transaction, $0/month.</li>
                    </ul>

                    <p>Devotion Fund is 5% on bank transfers, 7% on card.</p>

                    <p>Run the math on a typical gift and CTG often comes out ahead on raw fees:</p>

                    <ul>
                        <li><strong>A $100 monthly gift, independent missionary on CTG:</strong> $3.90 + $0.45 = <strong>$4.35</strong> (about 4.35%).</li>
                        <li><strong>The same $100 gift on Devotion Fund:</strong> <strong>$5.00 by bank transfer, or $7.00 by card</strong>.</li>
                        <li><strong>If your sending org uses CTG (2.7% + $0.25):</strong> <strong>$2.95</strong> on that same $100.</li>
                    </ul>

                    <p>So if your single decision criterion is "lowest fee per dollar," and especially if your sending organization already runs on Continue to Give, CTG can be the cheaper platform. I'd rather you hear that from me than discover it after you've moved.</p>

                    <p>One honest footnote on their marketing: CTG's missionary page headline says "rates as low as 0.5%." The tiers they actually publish for missionaries are 2.7% + $0.25 and 3.9% + $0.45. The 0.5% appears to describe a best-case platform-only sliver under specific sending-org arrangements, not what a normal individual missionary pays. It's not dishonest, but don't budget around the 0.5%; budget around the tier you actually qualify for.</p>

                    <h2>Where Continue to Give is genuinely the better choice</h2>

                    <p>I want to be specific here, not grudging.</p>

                    <p><strong>1. If your sending organization already uses CTG.</strong> This is the clearest case. You get the 2.7% + $0.25 rate, you get tax-deductible receipting through the org, and you're not asking your donors to learn a new system. Don't switch. Stay where the rails already run.</p>

                    <p><strong>2. If tax-deductibility is decisive for your donors.</strong> Through a sending organization, CTG can issue tax-deductible receipts. Devotion Fund's recurring partnership model is, today, not tax-deductible. For most monthly partners giving $25–$100, deductibility rarely changes the decision — but if you have larger donors who itemize and need the deduction, that's a real reason to choose CTG (or a fiscal-sponsor platform), and I won't talk you out of it.</p>

                    <p><strong>3. If you need full fund accounting and a church-grade donor database.</strong> CTG isn't just a giving page — it's an all-in-one with a real fund-accounting system, pledge management, events, child check-in, and a full ChMS. If you're effectively running a small organization, not just raising personal support, that depth is worth paying their monthly module fees for. We don't build that, and we're not trying to.</p>

                    <p><strong>4. If 15 years of track record matters more to you than anything new.</strong> Fair. We launched in 2026. They've been doing this since 2010. If you want a platform that has already survived a decade and a half, that's a legitimate thing to weigh, and "we're new" is the honest answer.</p>

                    <h2>Where Devotion Fund is the better choice</h2>

                    <p>Now my side of it.</p>

                    <p><strong>1. We're built for one person, not three audiences.</strong> Continue to Give serves churches, nonprofits, and missionaries on the same underlying chassis. That's a strength for breadth and a cost in focus — a lot of the product is church-and-nonprofit machinery a solo missionary will never touch. Devotion Fund is built for support-raised individuals and nothing else.</p>

                    <p><strong>2. Recurring is the default, not a setting.</strong> Most platforms treat recurring giving as a checkbox on a fundraising page. Our entire model assumes that the relationship between you and a monthly partner is the product — the giving page, the updates, the donor view, all of it is designed around the year-after-year partnership, not the one-time gift.</p>

                    <p><strong>3. Updates are built in, with AI help.</strong> The single biggest reason support dries up isn't fees — it's that the newsletter stops. Donors give to people they hear from. We build the update tooling directly into the platform, with AI-assisted drafting so the monthly update takes twenty minutes instead of getting skipped for three months. On CTG, newsletters live inside the paid ChMS module. For us it's core. (More on writing the update itself: <a href="/resources/how-to-write-a-prayer-letter-people-actually-read">how to write a prayer letter people actually read</a>.)</p>

                    <p><strong>4. The fee is two numbers, not tiers.</strong> 5% on bank transfer, 7% on card — no "via sending org" tier, no independent tier, no separate add-on pricing for the donor-management piece, no asterisk on whether you qualify for the headline rate. You can explain our fee to a donor in one sentence.</p>

                    <h2>Where Devotion Fund is weaker (and I mean it)</h2>

                    <ul>
                        <li><strong>We're more expensive on raw fees</strong> than CTG's missionary tiers, full stop. If fee-per-dollar is your only metric, we lose this one.</li>
                        <li><strong>We don't do tax-deductible receipts yet.</strong> If that's a dealbreaker for your donor base, we're not your platform today.</li>
                        <li><strong>We have no fund accounting.</strong> If you need a registrar, journal entries, and fund transfers, CTG has a real one and we have none.</li>
                        <li><strong>We're new.</strong> No 15-year history, fewer integrations, a smaller feature surface.</li>
                    </ul>

                    <h2>So which one should you actually pick?</h2>

                    <p>A simple filter:</p>

                    <ul>
                        <li><strong>Your sending org already uses Continue to Give</strong> → stay on CTG. Easy call.</li>
                        <li><strong>Tax-deductibility is decisive for your donors, or you need fund accounting</strong> → Continue to Give (or a 501(c)(3) fiscal-sponsor platform).</li>
                        <li><strong>You're an independent missionary raising recurring monthly support, your donors mostly give $25–$100, and what you actually struggle with is keeping partners engaged month after month</strong> → that's exactly who we built Devotion Fund for.</li>
                    </ul>

                    <p>The fee is the easy thing to compare, so it's the thing everyone compares. But the missionaries who lose support rarely lose it to fees. They lose it to silence. Pick the platform that makes it easiest for you to keep showing up in your partners' inboxes — whichever one that is.</p>

                    <p>If that's us, <a href="/">start a page on Devotion Fund</a>. If it's Continue to Give, genuinely — go raise your support and don't look back.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is Continue to Give cheaper than Devotion Fund?</strong><br />
                    Usually, on raw fees. CTG charges 2.7% + $0.25 (via a sending org) or 3.9% + $0.45 (independent), both typically lower than Devotion Fund's 5-7% at common gift sizes. Devotion Fund's fee is a single all-in number (5% bank transfer, 7% card) with no tiers.</p>

                    <p><strong>Does Continue to Give offer tax-deductible donations?</strong><br />
                    Yes, when processed through a sending organization. Devotion Fund's recurring partnership giving is not currently tax-deductible.</p>

                    <p><strong>Which is better for an independent missionary with no sending organization?</strong><br />
                    If keeping supporters engaged long-term is the harder problem than the fee difference, Devotion Fund's recurring-first design and built-in update tooling are built for exactly that. If the lowest possible fee and full fund accounting matter more, Continue to Give wins.</p>

                    <h2>Next reads</h2>
                    <ul>
                        <li><a href="/resources/gofundme-alternatives-for-missionaries">GoFundMe Alternatives for Missionaries: 7 Platforms Compared</a></li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page as a Missionary (Without Becoming a 501(c)(3))</a></li>
                        <li><a href="/resources/how-to-write-a-prayer-letter-people-actually-read">How to Write a Prayer Letter People Actually Read</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind Devotion Fund. We write these comparisons because we went looking for honest ones when we started and couldn't find any. If we've gotten a number wrong about Continue to Give, email us and we'll correct it — same as we did above.</p>
                    <p><small><em>Last updated: 2026-07-20. Continue to Give pricing verified against their published missionary pricing page.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/continue-to-give-vs-devotion-fund" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
